import React from 'react';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { getAccentColor } from './shared';

const PrincipalMessageSection = ({
  section,
  sectionKey,
  sectionIndex,
  bodyContent,
  renderPositionableElement,
}) => {
  const accent = getAccentColor(section) || '#059669';

  return (
    <Box component="section" sx={{ py: { xs: 1, md: 1.5 }, width: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1, md: 1.1 },
          gridTemplateColumns: { xs: '1fr', md: '320px minmax(0, 1fr)' },
          alignItems: 'stretch',
        }}
      >
        {renderPositionableElement({
          section,
          index: sectionIndex,
          elementKey: `${sectionKey}-principal-image`,
          children: section.image ? (
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '30px',
                minHeight: { xs: 300, md: 420 },
                background: `linear-gradient(160deg, ${alpha(accent, 0.18)} 0%, rgba(255,255,255,0.96) 100%)`,
              }}
            >
              <Box
                component="img"
                src={section.image}
                alt={section.title || ''}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: section.imageObjectPosition || 'center top',
                  display: 'block',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(180deg, rgba(15,23,42,0.02) 0%, rgba(15,23,42,0.2) 100%)',
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                minHeight: { xs: 280, md: 420 },
                display: 'grid',
                placeItems: 'center',
                borderRadius: '30px',
                border: `2px dashed ${alpha(accent, 0.28)}`,
                background: `linear-gradient(160deg, ${alpha(accent, 0.08)} 0%, #F8FAFC 100%)`,
              }}
            >
              <Typography sx={{ color: alpha('#0F172A', 0.45), fontWeight: 700 }}>
                Add image
              </Typography>
            </Box>
          ),
        })}

        {renderPositionableElement({
          section,
          index: sectionIndex,
          elementKey: `${sectionKey}-principal-card`,
          children: (
            <Box
              sx={{
                position: 'relative',
                borderRadius: '30px',
                overflow: 'hidden',
                minHeight: { xs: 'auto', md: 420 },
                background:
                  'linear-gradient(135deg, rgba(255,248,243,0.98) 0%, rgba(255,255,255,0.98) 52%, rgba(243,248,255,0.98) 100%)',
                border: `1px solid ${alpha(accent, 0.1)}`,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: { xs: 5, md: 8 },
                  background: `linear-gradient(180deg, ${accent} 0%, ${alpha(accent, 0.46)} 100%)`,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: -38,
                  right: -22,
                  width: 150,
                  height: 150,
                  borderRadius: '6px',
                  background: alpha(accent, 0.08),
                }}
              />

              <Stack
                spacing={1.5}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  height: '100%',
                  p: { xs: 2, md: 2.6 },
                  justifyContent: 'space-between',
                }}
              >
                <Stack spacing={1.15}>
                  <Box
                    sx={{
                      px: 1.05,
                      py: 0.5,
                      borderRadius: '6px',
                      backgroundColor: alpha(accent, 0.1),
                      color: accent,
                      width: 'fit-content',
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Message
                  </Box>

                  {section.title ? (
                    <Typography
                      sx={{
                        color: '#0F172A',
                        fontWeight: 860,
                        fontSize: { xs: '1.45rem', md: '2rem' },
                        lineHeight: 1.14,
                        letterSpacing: '-0.03em',
                        maxWidth: 760,
                      }}
                    >
                      {section.title}
                    </Typography>
                  ) : null}

                  {bodyContent ? (
                    <Box
                      sx={{
                        color: '#475569',
                        fontSize: { xs: '0.96rem', md: '1rem' },
                        lineHeight: 1.8,
                        maxWidth: 820,
                      }}
                    >
                      {bodyContent}
                    </Box>
                  ) : null}
                </Stack>

                {(section.buttonLabel || section.buttonPath) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.2,
                      pt: 1.2,
                      borderTop: `1px solid ${alpha('#0F172A', 0.08)}`,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Stack spacing={0.35}>
                      {section.buttonLabel ? (
                        <Typography
                          sx={{
                            color: '#0F172A',
                            fontWeight: 820,
                            fontSize: { xs: '1rem', md: '1.05rem' },
                          }}
                        >
                          {section.buttonLabel}
                        </Typography>
                      ) : null}
                      {section.buttonPath ? (
                        <Typography
                          sx={{
                            color: accent,
                            fontWeight: 800,
                            fontSize: '0.78rem',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {section.buttonPath}
                        </Typography>
                      ) : null}
                    </Stack>

                    <Box
                      sx={{
                        px: 1.1,
                        py: 0.62,
                        borderRadius: '6px',
                        backgroundColor: alpha(accent, 0.1),
                        color: accent,
                        fontWeight: 800,
                        fontSize: '0.8rem',
                        letterSpacing: '0.04em',
                      }}
                    >
                      School Leadership
                    </Box>
                  </Box>
                ) : null}
              </Stack>
            </Box>
          ),
        })}
      </Box>
    </Box>
  );
};

export default PrincipalMessageSection;
